
<template>
  <div v-if="['J', '1225-onboarding-core-v1', 'K'].includes(onboardingVariant)">
    <div id="express-checkout-element">
      <!-- Mount the Express Checkout Element here -->
    </div>
  </div>
  <div
    v-else
    id="payment-request-button"
    ref="paymentRequestButton"
    :class="{'onboarding-redesign tw-relative': isOnboardingRedesign}"
  ></div>
</template>

<script>
import { mapState } from 'vuex';
import { PatientService } from '@/services/patient.service';
import { waitCallbackTruthy } from '@/utils/waitCallbackTruthy';

export default {
  props: {
    totalPrice: {
      type: [Number, null],
      default: null
    },
  },
  data() {
    return {
      pk: process.env.VUE_APP_STRIPE_CLIENT_KEYS,
      canMakePayment: false,
      setupIntentClientSecret: null,
      stripeElements: null,
    }
  },
  computed: {
    ...mapState('global', ['isOnboardingRedesign']),
    ...mapState('onboarding', ['onboardingPatient']),
    onboardingVariant() {
      return this.onboardingPatient?.flow_variant;
    },
  },
  watch: {
    totalPrice(newVal) {
      if (['J', '1225-onboarding-core-v1', 'K'].includes(this.onboardingVariant) && newVal > 0) {
        this.$nextTick(() => {
          this.loadPaymentRequest();
        });
      }
    },
    onboardingVariant(newVal) {
      if (['J', '1225-onboarding-core-v1', 'K'].includes(newVal) && this.totalPrice > 0) {
        this.$nextTick(() => {
          this.loadPaymentRequest();
        });
      } else if (newVal && !['J', '1225-onboarding-core-v1', 'K'].includes(newVal) && this.totalPrice !== null) {
        this.loadPaymentRequest();
      }
    }
  },
  async mounted () {
    await waitCallbackTruthy(() => this.totalPrice !== null && this.onboardingVariant)
    if (['J', '1225-onboarding-core-v1', 'K'].includes(this.onboardingVariant) && this.totalPrice > 0) {
      await this.$nextTick();
      this.loadPaymentRequest();
    } else {
      this.loadPaymentRequest();
    }
  },
  methods: {
    async loadPaymentRequest() {
      const stripe = Stripe(this.pk);

      if (['J', '1225-onboarding-core-v1', 'K'].includes(this.onboardingVariant)) {
        try {
          const { data } = await PatientService.getStripeSetupIntentClientSecret();
          this.setupIntentClientSecret = data.body.client_secret;
        } catch (error) {
          console.error('Error creating SetupIntent:', error);
          return;
        }

        const options = {
          mode: 'setup',
          currency: 'usd',
          setup_future_usage: 'off_session',
        };
  
        const elements = stripe.elements(options);
        this.stripeElements = elements;
        const expressCheckoutElement = elements.create('expressCheckout');
        expressCheckoutElement.mount('#express-checkout-element');
  
        expressCheckoutElement.on('click', event => {
          const options = {
            emailRequired: true,
            shippingAddressRequired: true,
            phoneNumberRequired: true,
            shippingRates: [{
              id: 'usps',
              displayName: 'USPS Ground Advantage',
              amount: 0,
            }],
          };
          event.resolve(options);
        });
        
        expressCheckoutElement.on('ready', ({ availablePaymentMethods }) => {
          this.canMakePayment = availablePaymentMethods != null &&
            (availablePaymentMethods?.applePay || availablePaymentMethods?.googlePay || availablePaymentMethods?.link);
          this.$emit('canMakePayment', this.canMakePayment);
        });
  
        expressCheckoutElement.on('confirm', async event => {
          try {
            const { error, setupIntent } = await stripe.confirmSetup({
              elements,
              clientSecret: this.setupIntentClientSecret,
              confirmParams: {
                return_url: window.location.href,
              },
              redirect: 'if_required',
            });
  
            if (error) {
              console.error('Setup confirmation error:', error);
              throw error;
            } else {
              const formattedEvent = {
                shippingAddress: {
                  address: {
                    line1: event.shippingAddress?.address?.line1 || '',
                    city: event.shippingAddress?.address?.city || '',
                    state: event.shippingAddress?.address?.state || '',
                    country: event.shippingAddress?.address?.country || '',
                    postal_code: event.shippingAddress?.address?.postal_code || '',
                  }
                },
                billingDetails: {
                  phone: event.billingDetails?.phone || event.phone || '',
                },
                paymentMethod: setupIntent.payment_method,
                setupIntent: setupIntent,
              };
              
              this.$emit('success', formattedEvent);
            }
          } catch (err) {
            console.error('Error during setup confirmation:', err);
            throw err;
          }
        });
      } else {
        const paymentRequest = stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: 'Total',
            amount: this.totalPrice * 100,
          },
          requestPayerName: true,
          requestPayerEmail: true,
          requestPayerPhone: true,
          requestShipping: true,
          shippingOptions: [{
            id: 'usps',
            label: 'USPS Ground Advantage',
            detail: 'USPS Ground Advantage',
            amount: 0,
          }],
        });

        const elements = stripe.elements();
        const prButton = elements.create('paymentRequestButton', {
          paymentRequest: paymentRequest,
        });
        // Check the availability of the Payment Request API first.
        paymentRequest.canMakePayment().then(result => {
          this.canMakePayment =
          result != null &&
          (result?.applePay || result?.googlePay || result?.link);
          this.$emit('canMakePayment', this.canMakePayment);
          if (result) {
            prButton.mount('#payment-request-button');
          } else {
            document.getElementById('payment-request-button').style.display =
            'none';
          }
        });

        paymentRequest.on('paymentmethod', ev => {
          this.$emit('success', ev)
          ev.complete('success');
        });
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@media (min-width: 769px) {
  #payment-request-button.onboarding-redesign {
    padding-top: 0.15rem !important;
    transform: scaleX(1.05) scaleY(1.1) translateX(5px);
  }
}
</style>